import ResendSmsInfo from '@/pages/components/resend-sms/ResendSmsInfo';
import type { PatchPatientForInstantConsultationReply } from '@/pages/NurseConsultation';
import { Button, Dialog, Typography } from '@montugroup/design-system';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import { Box, DialogContent, DialogTitle, IconButton, Slide, styled } from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import React, { useEffect, useRef, useState } from 'react';
import { ResendSmsButton } from './ResendSmsButton';

interface ResendSmsVerificationDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => Promise<PatchPatientForInstantConsultationReply>;
  handleShowCalendar: () => void;
  sendSmsRetryCount: number;
  setSendSmsRetryCount: (count: number) => void;
}

const StyledButton = styled(Button)(({ theme }) => ({
  '& .MuiTypography-root': {
    color: theme.palette.common.white,
  },
  '& .MuiSvgIcon-root': {
    color: theme.palette.common.white,
  },
}));

const ResendSmsInfoContainer = styled(Box)(() => ({
  flex: 1,
}));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const usePrevious = <T,>(value: T): T | undefined => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
};

const ResendSmsVerificationDialog = ({
  open,
  onClose,
  onSubmit,
  handleShowCalendar,
  sendSmsRetryCount,
  setSendSmsRetryCount,
}: ResendSmsVerificationDialogProps) => {
  // When the dialog opens and the user clicks on the resend button,
  // we don't want the info shown on the dialog to change right away.
  // Instead, we will show the new info when the dialog is closed and reopened.
  // This is why we need to use usePrevious - we need to track whether the dialog
  // has been opened or not.
  const [dialogInfoIndex, setDialogInfoIndex] = useState(sendSmsRetryCount);
  const prevOpen = usePrevious(open);

  useEffect(() => {
    if (!prevOpen && open) {
      setDialogInfoIndex(sendSmsRetryCount);
    }
  }, [open, prevOpen, sendSmsRetryCount]);

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          width: '100%',
          height: '80vh',
          maxHeight: '80vh',
          margin: 'auto',
          padding: 2,
          display: 'flex',
          flexDirection: 'column',
          bottom: 0,
          position: 'fixed',
          borderRadius: '24px 24px 0 0',
        },
      }}
    >
      <DialogTitle sx={{ position: 'relative', pr: 5 }}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <ResendSmsInfoContainer>
          <ResendSmsInfo
            dialogInfoIndex={dialogInfoIndex}
            sendSmsRetryCount={sendSmsRetryCount}
            setSendSmsRetryCount={setSendSmsRetryCount}
          />
        </ResendSmsInfoContainer>
        <Box display="flex" flexDirection="column" gap={8} marginTop={24} marginBottom={8}>
          <ResendSmsButton onSubmit={onSubmit} sendSmsRetryCount={sendSmsRetryCount} />
          <StyledButton
            type="button"
            color="secondary"
            size="large"
            variant="contained"
            onClick={handleShowCalendar}
            startIcon={<CalendarTodayIcon />}
          >
            <Typography>Book consultation</Typography>
          </StyledButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ResendSmsVerificationDialog;
